// frontend/src/pages/employees/BreakdownPanel.jsx
import React, { useEffect, useRef, useState } from "react";
import { hours as fmtHours } from "./format";
import { useAnalytics } from "./useAnalytics";

export const DIMENSIONS = [
  { value: "client", label: "Client" },
  { value: "time_code", label: "Time code" },
  { value: "task", label: "Task" },
];

const COLLAPSED = 8;

const panel = {
  background: "white", borderRadius: 12, padding: 24,
  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
};
const segment = {
  display: "inline-flex", border: "1px solid #e5e7eb", borderRadius: 8,
  overflow: "hidden", marginBottom: 16,
};

function segmentButton(active) {
  return {
    padding: "6px 14px", border: "none", cursor: "pointer", fontSize: 13,
    fontWeight: 600,
    background: active ? "#eff6ff" : "white",
    color: active ? "#1d4ed8" : "#6b7280",
  };
}

const rowButton = {
  display: "grid", gridTemplateColumns: "1fr auto 120px", alignItems: "center",
  gap: 12, width: "100%", padding: "8px 4px", background: "none",
  border: "none", borderBottom: "1px solid #f3f4f6", cursor: "pointer",
  fontSize: 14, textAlign: "left", font: "inherit",
};

/**
 * Where the time went, by one dimension at a time.
 *
 * Absorbs the old Time Codes tab and the old Details tab. Owns its own
 * fetch and its own error state, so a 500 here shows a message in this
 * panel and leaves the rest of the page standing.
 */
export default function BreakdownPanel({
  employeeId, startDate, endDate, onRowClick, onTotals,
}) {
  const [by, setBy] = useState("client");
  const [expanded, setExpanded] = useState(false);

  const params = { by, start_date: startDate, end_date: endDate };
  if (employeeId) params.employee = employeeId;

  const { data, loading, error } = useAnalytics("breakdown/", params);

  // Hand totals up rather than fetching them twice. The tiles and this
  // panel are the same aggregate over the same rows; two requests could
  // disagree, and two disagreeing totals on one accounting screen is the
  // exact failure the 2026-09-23 work had to fix.
  //
  // onTotals is read through a ref, kept current every render, so the
  // effect below can depend on `data` alone. A caller that passes a new
  // onTotals identity every render (no useCallback) must not be able to
  // make this effect refire and loop.
  const onTotalsRef = useRef(onTotals);
  useEffect(() => { onTotalsRef.current = onTotals; });

  useEffect(() => {
    if (data && data.totals && onTotalsRef.current) onTotalsRef.current(data.totals);
  }, [data]);

  // Collapse again when the dimension changes -- "show all 14" makes no
  // promise about the next dimension's row count.
  useEffect(() => { setExpanded(false); }, [by]);

  const rows = (data && data.rows) || [];
  const shown = expanded ? rows : rows.slice(0, COLLAPSED);
  const max = rows.reduce((m, r) => Math.max(m, Number(r.hours) || 0), 0);

  return (
    <div style={panel}>
      <h3 style={{ marginTop: 0, marginBottom: 12, fontSize: 16 }}>
        Where the time went
      </h3>

      <div style={segment} role="group" aria-label="Breakdown dimension">
        {DIMENSIONS.map((d) => (
          <button
            key={d.value} type="button" aria-pressed={by === d.value}
            onClick={() => setBy(d.value)} style={segmentButton(by === d.value)}
          >
            {d.label}
          </button>
        ))}
      </div>

      {loading && <div style={{ color: "#6b7280" }}>Loading…</div>}
      {error && (
        <div role="alert" style={{ color: "#b91c1c" }}>{error}</div>
      )}

      {!loading && !error && rows.length === 0 && (
        <div style={{ color: "#6b7280" }}>No time in this period.</div>
      )}

      {!loading && !error && shown.map((r) => (
        <button
          key={`${r.id}`}
          type="button"
          style={rowButton}
          onClick={() => onRowClick && onRowClick({
            by,
            // A placeholder row has a null id -- its LABEL is what the
            // backend recognises as "this field is empty", so that is
            // what has to travel or the drawer opens on nothing.
            id: r.id === null || r.id === undefined ? r.label : r.id,
            label: r.label,
            hours: r.hours,
          })}
        >
          <span style={{ fontWeight: 600 }}>{r.label}</span>
          <span>{fmtHours(r.hours)}</span>
          <span aria-hidden="true" style={{
            height: 8, background: "#e5e7eb", borderRadius: 4,
            overflow: "hidden",
          }}>
            <span style={{
              display: "block", height: "100%", background: "#3b82f6",
              width: max > 0 ? `${(Number(r.hours) / max) * 100}%` : "0%",
            }} />
          </span>
        </button>
      ))}

      {!loading && !error && rows.length > COLLAPSED && !expanded && (
        <button
          type="button" onClick={() => setExpanded(true)}
          style={{ marginTop: 12, background: "none", border: "none",
                   color: "#3b82f6", cursor: "pointer", fontSize: 13,
                   fontWeight: 600, padding: 0 }}
        >
          ▸ Show all {rows.length}
        </button>
      )}
    </div>
  );
}
